import { useMemo } from 'react'

import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntityDerive, useValidationErrorEntityDerive } from 'src/hooks/useEntityDerive'
import { ItemDTO } from 'src/models/dto/items/ItemDTO'
import { Locale } from 'src/models/dto/Locale'
import { ValidationErrorEntity } from 'src/models/dto/TemplateValidationError'

export interface CommonItemEditorProps {
    itemId: string
    locale: Locale
    showValidationError?: boolean
    editDisabled?: boolean
}

export interface ProvidedItemEditorProps {
    itemId: string
    locale: Locale
    showError?: boolean
    disabled?: boolean
    validationErrorMessage?: string
}

export interface ItemEntityFieldProps<T, R> extends CommonItemEditorProps {
    workflowEntityId: string
    derive: (entity: T) => R
    deriveValidationErrorMessage?: (errors: ValidationErrorEntity) => string | undefined
    children: (params: { value: R; props: ProvidedItemEditorProps }) => JSX.Element
    /**
     * If set to `true`, this field can be edited on all locales.
     * This is often true for non-text contents.
     */
    enabledOnAllLocales?: boolean
}

const returnUndefined = () => undefined
/**
 * Wrapper component with a render prop to render a form field, based on
 * an item entity's derived value and validation errors.
 */
export const ItemEntityField = <T extends ItemDTO, R>(props: ItemEntityFieldProps<T, R>) => {
    const {
        derive,
        deriveValidationErrorMessage,
        children: render,
        itemId,
        locale,
        showValidationError,
        editDisabled,
        enabledOnAllLocales = false,
        workflowEntityId,
    } = props

    const value = useItemEntityDerive<R, T>({
        entityId: itemId,
        workflowEntityId,
        derive,
    })

    const validationErrorMessage = useValidationErrorEntityDerive<string | undefined>({
        validationErrorEntityId: itemId,
        derive: deriveValidationErrorMessage ?? returnUndefined,
    })

    const innerProps = useMemo<ProvidedItemEditorProps>(
        () => ({
            itemId,
            locale,
            showError: showValidationError,
            disabled: editDisabled || (!enabledOnAllLocales && !isDefaultLocale(locale)),
            validationErrorMessage,
        }),
        [
            itemId,
            locale,
            showValidationError,
            editDisabled,
            enabledOnAllLocales,
            validationErrorMessage,
        ]
    )

    return render({ value, props: innerProps })
}
